/**
 * Guest access tokens are short-lived credentials to one booking. They live in
 * sessionStorage, not localStorage: the convenience only needs to last as long as the tab
 * the guest booked in, and nothing is left behind on a shared computer once it is closed.
 */

const MANAGE_KEY = 'sazerac.manage-token';
const CONCIERGE_KEY = 'sazerac.concierge-token';
const LAUNDRY_KEY = 'sazerac.laundry-token';

function read(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    // Private mode or blocked storage: fall back to the URL token only.
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value) window.sessionStorage.setItem(key, value);
    else window.sessionStorage.removeItem(key);
  } catch {
    /* storage unavailable — the token in the URL still works */
  }
}

export const storeManageToken = (token: string | null) => write(MANAGE_KEY, token);
export const readManageToken = () => read(MANAGE_KEY);

export const storeConciergeToken = (token: string | null) => write(CONCIERGE_KEY, token);
export const readConciergeToken = () => read(CONCIERGE_KEY);

export const storeLaundryToken = (token: string | null) => write(LAUNDRY_KEY, token);
export const readLaundryToken = () => read(LAUNDRY_KEY);

export function clearGuestTokens() {
  write(MANAGE_KEY, null);
  write(CONCIERGE_KEY, null);
  write(LAUNDRY_KEY, null);
}

/**
 * Reads a token from the query string and removes it from the visible URL, so a booking
 * link is not left in the address bar, browser history or a copied URL.
 */
export function consumeTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (!token) return null;

  params.delete('token');
  const query = params.toString();
  window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  return token;
}
